import type { ApiClient } from './ApiClient';

// Formes alignées sur POST/GET /schools/:id/school-years, POST .../activate,
// POST .../deactivate, POST/GET .../phases (SCH-11,
// apps/api/src/routes/schoolYears.ts) — seule source de vérité pour ces routes.
export interface SchoolYear {
  id: string;
  label: string;
  startDate: string;
  endDate: string;
  createdAt: string;
}

export interface CreateSchoolYearPayload {
  label: string;
  startDate: string;
  endDate: string;
}

export interface CreateSchoolYearResponse {
  schoolYear: SchoolYear;
}

export function createSchoolYear(
  client: ApiClient,
  schoolId: string,
  payload: CreateSchoolYearPayload,
  accessToken: string,
): Promise<CreateSchoolYearResponse> {
  return client.post<CreateSchoolYearResponse>(`/schools/${schoolId}/school-years`, payload, { accessToken });
}

export interface ListSchoolYearsResponse {
  schoolYears: SchoolYear[];
  activeSchoolYearId: string | null;
}

export function listSchoolYears(
  client: ApiClient,
  schoolId: string,
  accessToken: string,
): Promise<ListSchoolYearsResponse> {
  return client.get<ListSchoolYearsResponse>(`/schools/${schoolId}/school-years`, { accessToken });
}

export interface UpdateSchoolYearPayload {
  startDate: string;
  endDate: string;
}

export interface UpdateSchoolYearResponse {
  schoolYear: SchoolYear;
}

// ENR-19 : corrige les dates d'une année scolaire déjà créée — `label` n'est PAS
// modifiable ici (hors périmètre de ce ticket, voir
// apps/api/src/routes/schoolYears.ts).
export function updateSchoolYear(
  client: ApiClient,
  schoolId: string,
  schoolYearId: string,
  payload: UpdateSchoolYearPayload,
  accessToken: string,
): Promise<UpdateSchoolYearResponse> {
  return client.patch<UpdateSchoolYearResponse>(`/schools/${schoolId}/school-years/${schoolYearId}`, payload, {
    accessToken,
  });
}

export interface ActivateSchoolYearResponse {
  schoolYear: SchoolYear;
}

// ⚠️ Rejette explicitement (409) si une AUTRE année est déjà active — jamais de
// bascule implicite. Voir apps/api/src/routes/schoolYears.ts pour le détail complet
// de cette décision produit (SCH-11).
export function activateSchoolYear(
  client: ApiClient,
  schoolId: string,
  schoolYearId: string,
  accessToken: string,
): Promise<ActivateSchoolYearResponse> {
  return client.post<ActivateSchoolYearResponse>(
    `/schools/${schoolId}/school-years/${schoolYearId}/activate`,
    undefined,
    { accessToken },
  );
}

export interface DeactivateSchoolYearResponse {
  activeSchoolYearId: null;
}

export function deactivateSchoolYear(
  client: ApiClient,
  schoolId: string,
  schoolYearId: string,
  accessToken: string,
): Promise<DeactivateSchoolYearResponse> {
  return client.post<DeactivateSchoolYearResponse>(
    `/schools/${schoolId}/school-years/${schoolYearId}/deactivate`,
    undefined,
    { accessToken },
  );
}

export interface EnrollmentPhase {
  id: string;
  label: string;
  opensAt: string;
  closesAt: string;
  createdAt: string;
}

export interface CreateEnrollmentPhasePayload {
  label: string;
  opensAt: string;
  closesAt: string;
}

export interface CreateEnrollmentPhaseResponse {
  phase: EnrollmentPhase;
}

// ⚠️ Rejette explicitement (409 `phase_overlap`) tout chevauchement de dates avec
// une phase existante de la MÊME année scolaire — voir
// apps/api/src/lib/enrollmentPhase.ts.
export function createEnrollmentPhase(
  client: ApiClient,
  schoolId: string,
  schoolYearId: string,
  payload: CreateEnrollmentPhasePayload,
  accessToken: string,
): Promise<CreateEnrollmentPhaseResponse> {
  return client.post<CreateEnrollmentPhaseResponse>(
    `/schools/${schoolId}/school-years/${schoolYearId}/phases`,
    payload,
    { accessToken },
  );
}

export interface ListEnrollmentPhasesResponse {
  phases: EnrollmentPhase[];
}

export function listEnrollmentPhases(
  client: ApiClient,
  schoolId: string,
  schoolYearId: string,
  accessToken: string,
): Promise<ListEnrollmentPhasesResponse> {
  return client.get<ListEnrollmentPhasesResponse>(`/schools/${schoolId}/school-years/${schoolYearId}/phases`, {
    accessToken,
  });
}

// ATT-05 — semaines calendaires ISO d'une année scolaire, générées
// AUTOMATIQUEMENT (jamais une configuration manuelle, contrairement aux
// phases ci-dessus). Voir apps/api/src/routes/schoolYears.ts, section
// "ATT-05". Ouvert aux TEACHER en plus des SCHOOL_ADMIN : nécessaire pour le
// sélecteur de semaine de l'écran de pointage de présence (ATT-02).
export interface SchoolWeek {
  id: string;
  schoolYearId: string;
  isoWeek: number;
  isoYear: number;
  startDate: string;
  endDate: string;
}

// ATT-07 : maillon accessible à TEACHER (CLASS_MANAGEMENT_ROLES côté backend) —
// contrairement à `listSchoolYears` ci-dessus (SCHOOL_ADMIN_ONLY), n'expose QUE
// l'ID actif, pas la liste complète des années.
export interface ActiveSchoolYearResponse {
  activeSchoolYearId: string | null;
}

export function getActiveSchoolYear(
  client: ApiClient,
  schoolId: string,
  accessToken: string,
): Promise<ActiveSchoolYearResponse> {
  return client.get<ActiveSchoolYearResponse>(`/schools/${schoolId}/active-school-year`, { accessToken });
}

export interface ListSchoolWeeksResponse {
  weeks: SchoolWeek[];
  /** Résout "quelle semaine sommes-nous actuellement" — `null` hors année scolaire. */
  currentWeekId: string | null;
}

export function listSchoolWeeks(
  client: ApiClient,
  schoolId: string,
  schoolYearId: string,
  accessToken: string,
): Promise<ListSchoolWeeksResponse> {
  return client.get<ListSchoolWeeksResponse>(`/schools/${schoolId}/school-years/${schoolYearId}/weeks`, {
    accessToken,
  });
}

// EVA-02 — périodes scolaires (trimestre/semestre) d'une année scolaire,
// CONFIGURÉES par le SCHOOL_ADMIN (contrairement aux semaines ci-dessus,
// générées automatiquement) : `putSchoolPeriods` remplace INTÉGRALEMENT la
// structure en une seule opération (2 ou 3 périodes), jamais un ajout
// incrémental comme `createEnrollmentPhase` ci-dessus. Voir
// apps/api/src/routes/schoolYears.ts, section "EVA-02".
export interface SchoolPeriod {
  id: string;
  label: string;
  startDate: string;
  endDate: string;
  order: number;
  createdAt: string;
}

export interface ListSchoolPeriodsResponse {
  periods: SchoolPeriod[];
  /** L'année scolaire est déjà TERMINÉE — périodes consultables mais non modifiables. */
  locked: boolean;
}

export function listSchoolPeriods(
  client: ApiClient,
  schoolId: string,
  schoolYearId: string,
  accessToken: string,
): Promise<ListSchoolPeriodsResponse> {
  return client.get<ListSchoolPeriodsResponse>(`/schools/${schoolId}/school-years/${schoolYearId}/periods`, {
    accessToken,
  });
}

export interface SchoolPeriodInput {
  label: string;
  startDate: string;
  endDate: string;
}

export interface PutSchoolPeriodsResponse {
  periods: SchoolPeriod[];
}

// ⚠️ Rejette explicitement 400 `invalid_period_count` (ni 2 ni 3 périodes),
// 400 `period_overlap` (chevauchement entre les périodes SOUMISES), et 409
// `school_year_ended` (année déjà terminée, AC#5) — messages backend affichés
// tels quels.
export function putSchoolPeriods(
  client: ApiClient,
  schoolId: string,
  schoolYearId: string,
  periods: SchoolPeriodInput[],
  accessToken: string,
): Promise<PutSchoolPeriodsResponse> {
  return client.put<PutSchoolPeriodsResponse>(
    `/schools/${schoolId}/school-years/${schoolYearId}/periods`,
    { periods },
    { accessToken },
  );
}
